import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE = {
  primary: '#ff6b35',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
};
const iconBox = (color, size = 36, radius = 10) => ({
  width: size,
  height: size,
  borderRadius: radius,
  background: `${color}12`,
  border: `1px solid ${color}30`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

/* ─── Shared card style (matches AssetInventoryTable) ───────────── */
const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const fmtAgo = (ts) => {
  if (!ts) return "—";
  const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diff < 1) return "Just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* ─── Severity helpers ───────────────────────────────────────────── */
const SEVERITY_META = {
  Critical: { color: "#8b0000", bg: "rgba(139,0,0,.1)", border: "rgba(139,0,0,.3)", dot: "#8b0000" },
  High: { color: "#dc3545", bg: "rgba(220,53,69,.1)", border: "rgba(220,53,69,.3)", dot: "#dc3545" },
  Medium: { color: "#d97706", bg: "rgba(217,119,6,.1)", border: "rgba(217,119,6,.3)", dot: "#d97706" },
  Low: { color: "#16a34a", bg: "rgba(22,163,74,.1)", border: "rgba(22,163,74,.3)", dot: "#16a34a" },
};

const getSeverityFromThreat = (threat) => {
  if (threat.asset_criticality) return threat.asset_criticality;
  const l = parseInt(threat.wazuh_rule_level) || 0;
  if (l >= 12) return "Critical";
  if (l >= 7) return "High";
  if (l >= 4) return "Medium";
  return "Low";
};

const SeverityBadge = ({ severity }) => {
  const m = SEVERITY_META[severity] || SEVERITY_META.Low;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: m.bg, color: m.color, border: `1px solid ${m.border}` }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.dot }} />
      {severity}
    </span>
  );
};

const RuleLevelBadge = ({ level }) => {
  const l = parseInt(level) || 0;
  const m = l >= 12 ? SEVERITY_META.Critical : l >= 7 ? SEVERITY_META.High : l >= 4 ? SEVERITY_META.Medium : SEVERITY_META.Low;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 28, height: 22, borderRadius: 6, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color, border: `1px solid ${m.border}`, padding: "0 6px" }}>
      {l}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const hash = (type || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const colors = [
    { bg: "rgba(255,107,53,.1)", color: "#ff6b35", border: "rgba(255,107,53,.3)" },
    { bg: "rgba(232,90,40,.1)", color: "#e85a28", border: "rgba(232,90,40,.3)" },
    { bg: "rgba(209,77,31,.1)", color: "#d14d1f", border: "rgba(209,77,31,.3)" },
    { bg: "rgba(255,140,97,.1)", color: "#ff8c61", border: "rgba(255,140,97,.3)" },
    { bg: "rgba(255,179,153,.1)", color: "#c97840", border: "rgba(255,179,153,.3)" },
  ];
  const c = colors[hash % colors.length];
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6, background: c.bg, color: c.color, border: `1px solid ${c.border}`, whiteSpace: "nowrap" }}>
      {type || "Unknown"}
    </span>
  );
};

/* ─── Stat tile ──────────────────────────────────────────────────── */
const StatTile = ({ icon, label, value, color, loading }) => (
  <div style={{ ...card, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
    <div style={iconBox(color, 38)}>
      <FeatherIcon icon={icon} size={18} style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: 22, fontWeight: 800, color, margin: 0, lineHeight: 1 }}>
        {loading ? "—" : (typeof value === "number" ? value.toLocaleString() : value)}
      </p>
      <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: "3px 0 0" }}>{label}</p>
    </div>
  </div>
);

/* ─── Chart card wrapper ─────────────────────────────────────────── */
const ChartCard = ({ title, icon, children }) => (
  <div style={{ ...card, height: "100%" }}>
    <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", gap: 8 }}>
      <div style={iconBox(ORANGE.primary, 26, 8)}>
        <FeatherIcon icon={icon} size={18} style={{ color: ORANGE.primary }} />
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>{title}</span>
    </div>
    <div style={{ padding: "12px" }}>{children}</div>
  </div>
);

/* ─── Main View ──────────────────────────────────────────────────── */
function View() {
  const location = useLocation();
  const navigate = useNavigate();
  const agentName = location.state?.agentName;

  const [summary, setSummary] = useState(null);
  const [threatTypes, setThreatTypes] = useState({});
  const [threatsByDate, setThreatsByDate] = useState({});
  const [heatmapData, setHeatmapData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [threats, setThreats] = useState([]);
  const [loadingThreats, setLoadingThreats] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalThreats, setTotalThreats] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");

  const getAuthHeaders = () => {
    const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
    return { "Content-Type": "application/json", ...(u.token ? { Authorization: `Bearer ${u.token}` } : {}) };
  };

  const fetchAgentSummary = useCallback(async (name) => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/threats/agents/${encodeURIComponent(name)}/details`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) {
        setSummary(d.summary);
        setThreatTypes(d.threat_types || {});
        setThreatsByDate(d.threats_by_date || {});
        setHeatmapData(d.heatmap_data || {});
      } else throw new Error(d.error);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const fetchThreats = useCallback(async (name, page, severity = severityFilter) => {
    setLoadingThreats(true);
    try {
      const r = await fetch(`${API_BASE_URL}/threats/agents/${encodeURIComponent(name)}/threats?page=${page}&limit=20&severity=${severity}`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) {
        setThreats(d.threats);
        setTotalThreats(d.pagination.total);
        setTotalPages(d.pagination.total_pages);
      }
    } catch (e) { setError(e.message); }
    finally { setLoadingThreats(false); }
  }, [severityFilter]);

  useEffect(() => {
    if (!agentName) { navigate("/AgentDashboard"); return; }
    fetchAgentSummary(agentName);
    fetchThreats(agentName, 1);
  }, [agentName, navigate, fetchAgentSummary, fetchThreats]);

  const handleSeverityChange = (s) => {
    setSeverityFilter(s); setCurrentPage(1);
    fetchThreats(agentName, 1, s);
  };

  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchThreats(agentName, p, severityFilter);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewThreat = (threat) => {
    navigate(`/ThreatGuard?id=${threat.threat_id}`);
  };

  const filteredThreats = useMemo(() => threats.filter(t => {
    const severity = getSeverityFromThreat(t);
    const q = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      (t.threat_type || "").toLowerCase().includes(q) ||
      (t.threat_description || "").toLowerCase().includes(q) ||
      (t.source_ip || "").includes(q) ||
      (t.wazuh_rule_id || "").toString().includes(q);
    const matchSev = severityFilter === "all" || severity.toLowerCase() === severityFilter;
    return matchSearch && matchSev;
  }), [threats, searchTerm, severityFilter]);

  /* ── Charts ────────────────────────────────────────────────────── */
  const isDark = document.documentElement.getAttribute("data-layout-mode") === "dark";
  const textColor = isDark ? "#a6b0cf" : "#858d98";

  const lineChart = useMemo(() => {
    const dates = Object.keys(threatsByDate).sort((a, b) => new Date(a) - new Date(b)).slice(-30);
    const data = dates.map(d => threatsByDate[d]);
    return (
      <ReactApexChart
        options={{
          chart: { height: 260, type: "line", toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
          stroke: { curve: "smooth", width: 2.5, colors: [ORANGE.primary] },
          fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 90, 100] } },
          markers: { size: 4, colors: [ORANGE.primary], strokeColors: "var(--vz-secondary-bg)", strokeWidth: 2, hover: { size: 6 } },
          dataLabels: { enabled: false },
          xaxis: { categories: dates, labels: { rotate: -45, style: { colors: textColor, fontSize: "10px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
          yaxis: { labels: { style: { colors: textColor } } },
          grid: { borderColor: "var(--vz-border-color)", strokeDashArray: 4 },
          tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} threats` } },
        }}
        series={[{ name: "Threats", data }]}
        type="area" height={260}
      />
    );
  }, [threatsByDate, textColor, isDark]);

  const donutChart = useMemo(() => {
    if (!summary) return null;
    return (
      <ReactApexChart
        options={{
          chart: { type: "donut", fontFamily: "inherit", background: "transparent" },
          labels: ["Critical", "High", "Medium", "Low"],
          colors: ["#8b0000", "#dc3545", "#d97706", "#16a34a"],
          dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: "600" } },
          legend: { position: "bottom", fontSize: "12px", labels: { colors: textColor } },
          plotOptions: { pie: { donut: { size: "68%", labels: { show: true, total: { show: true, label: "Total", fontSize: "13px", fontWeight: 700, color: ORANGE.primary, formatter: () => summary.total_threats?.toLocaleString() } } } } },
          stroke: { width: 2, colors: ["var(--vz-secondary-bg)"] },
          tooltip: { theme: isDark ? "dark" : "light" },
        }}
        series={[summary.critical || 0, summary.high || 0, summary.medium || 0, summary.low || 0]}
        type="donut" height={260}
      />
    );
  }, [summary, textColor, isDark]);

  const barChart = useMemo(() => {
    const types = Object.keys(threatTypes);
    const counts = Object.values(threatTypes);
    return (
      <ReactApexChart
        options={{
          chart: { toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
          plotOptions: { bar: { horizontal: true, barHeight: "55%", distributed: true, borderRadius: 4 } },
          dataLabels: { enabled: false },
          colors: [ORANGE.primary, ORANGE.dark, ORANGE.darker, ORANGE.light, ORANGE.lighter, "#e85a28", "#d14d1f", "#ff9d7a", "#c94219", "#ff7b47"],
          grid: { borderColor: "var(--vz-border-color)", strokeDashArray: 4 },
          legend: { show: false },
          xaxis: { categories: types, labels: { style: { colors: textColor } } },
          yaxis: { labels: { style: { colors: textColor, fontSize: "11px" } } },
          tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} threats` } },
        }}
        series={[{ name: "Threats", data: counts }]}
        type="bar" height={260}
      />
    );
  }, [threatTypes, textColor, isDark]);

  const heatChart = useMemo(() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const series = days.map((day, di) => ({
      name: day,
      data: hours.map(h => ({ x: `${h.toString().padStart(2, "0")}:00`, y: heatmapData[`${di}-${h}`] || 0 }))
    }));
    return (
      <ReactApexChart
        options={{
          chart: { type: "heatmap", toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
          dataLabels: { enabled: false },
          xaxis: { labels: { rotate: -45, style: { colors: textColor, fontSize: "9px" } } },
          yaxis: { labels: { style: { colors: textColor, fontSize: "11px" } } },
          grid: { borderColor: "var(--vz-border-color)" },
          plotOptions: {
            heatmap: {
              radius: 3, enableShades: true, shadeIntensity: 0.5,
              colorScale: {
                ranges: [
                  { from: 0, to: 0, color: "var(--vz-light, #f8f9fa)", name: "None" },
                  { from: 1, to: 10, color: "#bfdbfe", name: "Very Low" },
                  { from: 11, to: 30, color: "#93c5fd", name: "Low" },
                  { from: 31, to: 80, color: "#60a5fa", name: "Medium" },
                  { from: 81, to: 150, color: "#3b82f6", name: "High" },
                  { from: 151, to: 999999, color: "#1d4ed8", name: "Critical" },
                ]
              }
            }
          },
          tooltip: { theme: isDark ? "dark" : "light", y: { formatter: v => `${v} threats` } },
        }}
        series={series}
        type="heatmap" height={260}
      />
    );
  }, [heatmapData, textColor, isDark]);

  /* ── Loading / Error states ────────────────────────────────────── */
  if (loading && !summary) {
    return (
      <div className="page-content">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 12 }}>
          <Spinner color="primary" />
          <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, margin: 0 }}>Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="page-content">
        <div style={{ ...card, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12, color: "var(--vz-danger)", background: "rgba(var(--vz-danger-rgb),.06)", border: "1px solid rgba(var(--vz-danger-rgb),.3)" }}>
          <FeatherIcon icon="alert-circle" size={18} />
          <span style={{ flex: 1, fontSize: 13 }}>{error}</span>
          <button onClick={() => navigate("/AgentDashboard")}
            style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "var(--vz-danger)", color: "#fff", border: "none", cursor: "pointer" }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const severityOptions = [
    { val: "all", label: "All", color: ORANGE.primary, count: totalThreats },
    { val: "critical", label: "Critical", color: "#8b0000", count: summary?.critical },
    { val: "high", label: "High", color: "#dc3545", count: summary?.high },
    { val: "medium", label: "Medium", color: "#d97706", count: summary?.medium },
    { val: "low", label: "Low", color: "#16a34a", count: summary?.low },
  ];

  return (
    <div className="page-content">
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={{ animation: "fadeIn .3s ease" }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button onClick={() => navigate("/AgentDashboard")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "transparent", color: ORANGE.primary, border: `1px solid ${ORANGE.primary}`, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = ORANGE.primary; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE.primary; }}
            >
              <FeatherIcon icon="arrow-left" size={13} />Back
            </button>
            <div style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <div style={iconBox(ORANGE.primary, 44, 12)}>
                <FeatherIcon icon="monitor" size={18} style={{ color: ORANGE.primary }} />
              </div>

            </div>
            <div>
              <h4 style={{ fontWeight: 800, color: "var(--vz-body-color)", margin: 0, fontSize: 18 }}>{agentName}</h4>
              <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0 }}>
                Agent Details · {totalThreats.toLocaleString()} total threats
              </p>
            </div>
          </div>
          {/* Mini severity strip */}
          {summary && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {[
                { label: "Critical", value: summary.critical, color: "#8b0000" },
                { label: "High", value: summary.high, color: "#dc3545" },
                { label: "Medium", value: summary.medium, color: "#d97706" },
                { label: "Low", value: summary.low, color: "#16a34a" },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 20, fontWeight: 800, color: s.color, margin: 0, lineHeight: 1 }}>{s.value?.toLocaleString()}</p>
                  <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: "2px 0 0" }}>{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Summary tiles ────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <StatTile icon="shield" label="Total Threats" value={summary?.total_threats} color={ORANGE.primary} loading={loading} />
          <StatTile icon="alert-circle" label="Active" value={summary?.active} color="#dc3545" loading={loading} />
          <StatTile icon="check-circle" label="Resolved" value={summary?.resolved} color="#16a34a" loading={loading} />
          <StatTile icon="globe" label="Unique IPs" value={summary?.unique_ips} color="#0ea5e9" loading={loading} />
        </div>

        {/* ── Charts row 1 ─────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <ChartCard title="Threat Activity (30 days)" icon="trending-up">
            {lineChart}
          </ChartCard>
          <ChartCard title="Severity Distribution" icon="pie-chart">
            {donutChart}
          </ChartCard>
        </div>

        {/* ── Charts row 2 ─────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <ChartCard title="Threats by Type" icon="bar-chart-2">
            {barChart}
          </ChartCard>
          <ChartCard title="Activity Heatmap" icon="grid">
            {heatChart}
          </ChartCard>
        </div>

        {/* ── Threat Table ─────────────────────────────────────────── */}
        <div style={card}>
          {/* Table header */}
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Threat Details</span>
              <span style={{ fontSize: 12, padding: "2px 10px", background: `${ORANGE.primary}15`, borderRadius: 20, color: ORANGE.primary, fontWeight: 600 }}>
                {totalThreats.toLocaleString()}
              </span>
              {loadingThreats && <Spinner size="sm" color="primary" />}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* Search */}
              <div style={{ position: "relative" }}>
                <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
                <input
                  type="text" placeholder="Search threats..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: 30, paddingRight: searchTerm ? 28 : 12, height: 34, width: 220, border: "1px solid var(--vz-border-color)", borderRadius: 8, fontSize: 12, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", outline: "none" }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", padding: 0, fontSize: 13 }}>✕</button>
                )}
              </div>

              {/* Severity filter buttons */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {severityOptions.map(opt => {
                  const active = severityFilter === opt.val;
                  return (
                    <button key={opt.val} onClick={() => handleSeverityChange(opt.val)}
                      style={{ padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .15s", background: active ? opt.color : "transparent", color: active ? "#fff" : opt.color, border: `1px solid ${opt.color}` }}>
                      {opt.label}
                      {opt.count != null && <span style={{ marginLeft: 4, opacity: .8 }}>{opt.count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredThreats.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Threat Type", "Severity", "Lvl", "Rule ID", "Description", "Source IP", "Asset", "Time", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 14px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: "left", borderBottom: "1px solid var(--vz-border-color)", background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredThreats.map((threat, index) => {
                    const severity = getSeverityFromThreat(threat);
                    const globalIndex = ((currentPage - 1) * 20) + index + 1;
                    return (
                      <tr key={threat.threat_id || index}
                        style={{ borderBottom: "1px solid var(--vz-border-color)", transition: "background .15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        {/* # */}
                        <td style={{ padding: "11px 14px", fontSize: 12, fontWeight: 700, color: "var(--vz-secondary-color)" }}>
                          {globalIndex}
                        </td>

                        {/* Type */}
                        <td style={{ padding: "11px 14px" }}>
                          <TypeBadge type={threat.threat_type} />
                        </td>

                        {/* Severity */}
                        <td style={{ padding: "11px 14px" }}>
                          <SeverityBadge severity={severity} />
                        </td>

                        {/* Rule level */}
                        <td style={{ padding: "11px 14px" }}>
                          <RuleLevelBadge level={threat.wazuh_rule_level} />
                        </td>

                        {/* Rule ID */}
                        <td style={{ padding: "11px 14px" }}>
                          {threat.wazuh_rule_id ? (
                            <code style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: `${ORANGE.primary}12`, color: ORANGE.primary, border: `1px solid ${ORANGE.primary}30` }}>
                              {threat.wazuh_rule_id}
                            </code>
                          ) : <span style={{ color: "var(--vz-secondary-color)", opacity: .4 }}>—</span>}
                        </td>

                        {/* Description */}
                        <td style={{ padding: "11px 14px", maxWidth: 260 }}>
                          <div style={{ fontSize: 12, color: "var(--vz-body-color)", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            {threat.threat_description || threat.wazuh_rule_description || "No description"}
                          </div>
                        </td>

                        {/* Source IP */}
                        <td style={{ padding: "11px 14px" }}>
                          {threat.source_ip && threat.source_ip !== "N/A" ? (
                            <code style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: `${ORANGE.primary}10`, color: ORANGE.primary, border: `1px solid ${ORANGE.primary}25` }}>
                              {threat.source_ip}
                            </code>
                          ) : <span style={{ color: "var(--vz-secondary-color)", opacity: .4, fontSize: 11 }}>—</span>}
                        </td>

                        {/* Asset */}
                        <td style={{ padding: "11px 14px", fontSize: 12, color: "var(--vz-body-color)", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={iconBox(ORANGE.primary, 24, 50)}>
                              <FeatherIcon icon="monitor" size={11} style={{ color: ORANGE.primary }} />
                            </div>
                            {threat.asset_name || threat.agent_name || "—"}
                          </div>
                        </td>

                        {/* Time */}
                        <td style={{ padding: "11px 14px", fontSize: 11, color: "var(--vz-secondary-color)", whiteSpace: "nowrap" }}>
                          {fmtAgo(threat.timestamp)}
                        </td>

                        {/* View button */}
                        <td style={{ padding: "11px 14px" }}>
                          <button onClick={() => handleViewThreat(threat)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: "transparent", color: ORANGE.primary, border: `1px solid ${ORANGE.primary}`, cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap" }}
                            onMouseEnter={e => { e.currentTarget.style.background = ORANGE.primary; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE.primary; }}
                          >
                            <FeatherIcon icon="eye" size={12} />View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
              {loadingThreats ? (
                <>
                  <Spinner color="primary" />
                  <p style={{ marginTop: 10, fontSize: 13 }}>Loading threats...</p>
                </>
              ) : (
                <>
                  <FeatherIcon icon="inbox" size={40} style={{ opacity: .3, display: "block", margin: "0 auto 10px" }} />
                  <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>No threats found</p>
                  <p style={{ fontSize: 12, opacity: .6, margin: 0 }}>
                    {searchTerm || severityFilter !== "all" ? "Try adjusting your filters" : "No threat data available"}
                  </p>
                  {(searchTerm || severityFilter !== "all") && (
                    <button onClick={() => { setSearchTerm(""); setSeverityFilter("all"); }}
                      style={{ marginTop: 12, background: "none", border: "none", color: ORANGE.primary, fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
                      Clear filters
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { label: "«", page: 1, disabled: currentPage === 1 },
                  { label: "← Prev", page: currentPage - 1, disabled: currentPage === 1 },
                  { label: "Next →", page: currentPage + 1, disabled: currentPage === totalPages },
                  { label: "»", page: totalPages, disabled: currentPage === totalPages },
                ].map((btn, i) => (
                  <button key={i} onClick={() => !btn.disabled && handlePageChange(btn.page)}
                    disabled={btn.disabled || loadingThreats}
                    style={{ height: 32, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: btn.disabled ? "not-allowed" : "pointer", background: "transparent", color: btn.disabled ? "var(--vz-secondary-color)" : ORANGE.primary, border: `1px solid ${btn.disabled ? "var(--vz-border-color)" : ORANGE.primary}`, opacity: btn.disabled ? .5 : 1, transition: "all .15s" }}>
                    {btn.label}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                Page <strong style={{ color: ORANGE.primary }}>{currentPage}</strong> of <strong style={{ color: ORANGE.primary }}>{totalPages}</strong>
              </span>
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                <strong style={{ color: ORANGE.primary }}>{totalThreats.toLocaleString()}</strong> total threats
              </span>
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: "10px 18px", borderTop: "1px solid var(--vz-border-color)", background: `${ORANGE.primary}04`, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
              Showing <strong style={{ color: ORANGE.primary }}>{filteredThreats.length}</strong> of <strong style={{ color: ORANGE.primary }}>{threats.length}</strong> on this page
            </span>
            <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="clock" size={11} style={{ marginRight: 4 }} />
              {agentName} · Agent Detail View
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default View;